import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Loader2 } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { PageHeader, AdminCard, Field, Input, Textarea, Btn, PageLoader } from "@/admin/components";
import { Avatar, Switch, Chip } from "@/components/jk/kit";
import AvailabilityManager from "@/components/jk/AvailabilityManager";

const MODES = [["Video", "video"], ["Phone", "phone"], ["In-person", "in_person"]];
const SPECIALISATIONS = ["KP Astrology", "Nadi Astrology", "Tamil Astrology", "Vedic Astrology", "Numerology", "Vastu", "Palmistry", "Tarot", "Gemmology"];
const LANGUAGES = ["Tamil", "English", "Hindi", "Telugu", "Kannada", "Malayalam"];
const DURATIONS = [15, 30, 45, 60];

const emptyService = () => ({
  name: "", duration_minutes: 45, price_inr: 750, consultation_modes: ["video"],
  tier1_price_inr: "", tier1_duration_minutes: 45, tier1_consultation_modes: ["phone"],
  tier2_price_inr: "", tier2_duration_minutes: 45, tier2_consultation_modes: ["in_person"],
  tier3_price_inr: "", tier3_duration_minutes: 45, tier3_consultation_modes: ["phone", "in_person"],
});

const toggle = (list, item) => {
  const set = new Set(list);
  set.has(item) ? set.delete(item) : set.add(item);
  return [...set];
};

export default function AstrologerForm() {
  const navigate = useNavigate();
  const { aid } = useParams();
  const isEdit = !!aid;
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState("");

  const [profile, setProfile] = useState({
    full_name: "", email: "", city: "", photo_url: "", years_experience: "",
    about: "", specialisations: [], languages: [], instagram_url: "", youtube_url: "",
  });
  const [services, setServices] = useState([emptyService()]);
  const [payout, setPayout] = useState({ payout_upi_id: "", payout_account_name: "", payout_account_number: "", payout_ifsc: "" });
  const [active, setActive] = useState(true);
  const [sendInvite, setSendInvite] = useState(true);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await adminApi.get(`/admin/astrologers/${aid}/edit`);
        const a = data.astrologer;
        setProfile({
          full_name: a.full_name || "", email: a.email || "", city: a.city || "", photo_url: a.photo_url || "",
          years_experience: a.years_experience ?? "", about: a.about || "",
          specialisations: a.specialisations || [], languages: a.languages || [],
          instagram_url: a.instagram_url || "", youtube_url: a.youtube_url || "",
        });
        const svc = (data.services || []).map((s) => ({
          name: s.name, duration_minutes: s.duration_minutes, price_inr: s.price_inr,
          consultation_modes: s.consultation_modes || [],
          tier1_price_inr: s.tier1_price_inr ?? "", tier1_duration_minutes: s.tier1_duration_minutes ?? s.duration_minutes ?? 45, tier1_consultation_modes: s.tier1_consultation_modes ?? ["phone"],
          tier2_price_inr: s.tier2_price_inr ?? "", tier2_duration_minutes: s.tier2_duration_minutes ?? 45, tier2_consultation_modes: s.tier2_consultation_modes ?? ["in_person"],
          tier3_price_inr: s.tier3_price_inr ?? "", tier3_duration_minutes: s.tier3_duration_minutes ?? 45, tier3_consultation_modes: s.tier3_consultation_modes ?? ["phone", "in_person"],
        }));
        setServices(svc.length ? svc : [emptyService()]);
        setPayout({
          payout_upi_id: a.payout_upi_id || "", payout_account_name: a.payout_account_name || "",
          payout_account_number: a.payout_account_number || "", payout_ifsc: a.payout_ifsc || "",
        });
        setActive(a.profile_active !== false);
      } catch {
        toast.error("Couldn't load astrologer");
      } finally {
        setLoading(false);
      }
    })();
  }, [aid, isEdit]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError("");
    setPhotoUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await adminApi.post("/admin/upload-photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile((p) => ({ ...p, photo_url: data.photo_url }));
    } catch {
      setPhotoError("Photo upload failed. Please try again.");
    } finally {
      setPhotoUploading(false);
    }
  };

  const buildPayload = () => ({
    full_name: profile.full_name.trim(), email: profile.email.trim().toLowerCase(), city: profile.city.trim(),
    photo_url: profile.photo_url, specialisations: profile.specialisations, languages: profile.languages,
    years_experience: Number(profile.years_experience) || 0, about: profile.about.trim(),
    instagram_url: profile.instagram_url.trim(), youtube_url: profile.youtube_url.trim(),
    services: services.filter((s) => s.name.trim()).map((s) => ({
      name: s.name.trim(),
      price_inr: Number(s.tier1_price_inr) || Number(s.price_inr) || 0,
      duration_minutes: Number(s.tier1_duration_minutes) || Number(s.duration_minutes) || 45,
      tier1_price_inr: Number(s.tier1_price_inr) || null,
      tier1_duration_minutes: Number(s.tier1_duration_minutes) || null,
      tier1_consultation_modes: s.tier1_consultation_modes || [],
      tier2_price_inr: Number(s.tier2_price_inr) || null,
      tier2_duration_minutes: Number(s.tier2_duration_minutes) || null,
      tier2_consultation_modes: s.tier2_consultation_modes || [],
      tier3_price_inr: Number(s.tier3_price_inr) || null,
      tier3_duration_minutes: Number(s.tier3_duration_minutes) || null,
      tier3_consultation_modes: s.tier3_consultation_modes || [],
      consultation_modes: [...new Set([
        ...(s.tier1_consultation_modes || []),
        ...(s.tier2_consultation_modes || []),
        ...(s.tier3_consultation_modes || []),
      ])],
    })),
    ...payout, is_active: active, send_invite: sendInvite,
  });

  const submit = async () => {
    const payload = buildPayload();
    if (!payload.full_name) return toast.error("Enter the astrologer's name");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(payload.email)) return toast.error("Enter a valid email");
    if (payload.services.length === 0) return toast.error("Add at least one service");
    setSaving(true);
    try {
      if (isEdit) {
        await adminApi.put(`/admin/astrologers/${aid}`, payload);
        toast.success("Astrologer updated");
        navigate("/admin/astrologers");
      } else {
        const { data } = await adminApi.post("/admin/astrologers/create", payload);
        toast.success("Astrologer created — now set their availability");
        navigate(`/admin/astrologers/${data.astrologer_id}/edit`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save astrologer");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div data-testid="astrologer-form-page">
      <PageHeader
        title={isEdit ? "Edit astrologer" : "Create astrologer"}
        subtitle={isEdit ? "Profile, services, availability & payout" : "Step 1 — profile, services & payout. You'll set availability next."}
        right={<button onClick={() => navigate("/admin/astrologers")} className="flex items-center gap-1.5 text-[13px] font-medium text-[#777] hover:text-[#111]"><ArrowLeft size={15} /> Back</button>}
      />

      <div className="space-y-6 pb-28">
        {/* Profile */}
        <AdminCard className="p-6" testid="form-profile">
          <h3 className="mb-4 text-[15px] font-semibold text-[#111]">Profile</h3>
          <div className="mb-5 flex items-center gap-4">
            <div className="relative h-16 w-16 shrink-0">
              <Avatar name={profile.full_name} photo={profile.photo_url} size={64} />
              {photoUploading && (
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-white/70">
                  <Loader2 size={20} strokeWidth={1.5} className="animate-spin text-[#aaaaaa]" />
                </div>
              )}
            </div>
            <div>
              <label className={`cursor-pointer rounded-[10px] border border-[#e5e5e5] bg-white px-3.5 py-2 text-[13px] font-medium text-[#111] hover:bg-[#faf8f5] ${photoUploading ? "pointer-events-none opacity-50" : ""}`}>
                {photoUploading ? "Uploading…" : "Upload photo"}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="form-photo" />
              </label>
              {photoError && <p className="mt-1.5 text-[12px] text-red-500" data-testid="photo-error">{photoError}</p>}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Full name"><Input data-testid="form-name" value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} placeholder="e.g. Aditya Sastry" /></Field>
            <Field label="Login email"><Input data-testid="form-email" type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} placeholder="astrologer@email.com" /></Field>
            <Field label="City"><Input data-testid="form-city" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} placeholder="Chennai" /></Field>
            <Field label="Years of experience"><Input data-testid="form-years" type="number" min="0" value={profile.years_experience} onChange={(e) => setProfile({ ...profile, years_experience: e.target.value })} /></Field>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Specialisations">
              <div className="grid grid-cols-2 gap-2" data-testid="form-specialisations">
                {SPECIALISATIONS.map((s) => (
                  <Chip key={s} full selected={profile.specialisations.includes(s)} onClick={() => setProfile({ ...profile, specialisations: toggle(profile.specialisations, s) })} testid={`spec-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</Chip>
                ))}
              </div>
            </Field>
            <Field label="Languages">
              <div className="flex flex-wrap gap-2" data-testid="form-languages">
                {LANGUAGES.map((l) => (
                  <Chip key={l} selected={profile.languages.includes(l)} onClick={() => setProfile({ ...profile, languages: toggle(profile.languages, l) })} testid={`lang-${l.toLowerCase()}`}>{l}</Chip>
                ))}
              </div>
            </Field>
          </div>
          <div className="mt-4">
            <Field label="About">
              <Textarea data-testid="form-about" rows={3} maxLength={300} value={profile.about} onChange={(e) => setProfile({ ...profile, about: e.target.value })} placeholder="Short bio shown on the public profile" />
              <p className="mt-1 text-right text-[11px] text-[#aaaaaa]">{(profile.about || "").length} / 300 characters</p>
            </Field>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Instagram URL"><Input data-testid="form-instagram" type="url" value={profile.instagram_url} onChange={(e) => setProfile({ ...profile, instagram_url: e.target.value })} placeholder="https://instagram.com/handle" /></Field>
            <Field label="YouTube URL"><Input data-testid="form-youtube" type="url" value={profile.youtube_url} onChange={(e) => setProfile({ ...profile, youtube_url: e.target.value })} placeholder="https://youtube.com/@channel" /></Field>
          </div>
        </AdminCard>

        {/* Services */}
        <AdminCard className="p-6" testid="form-services">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-[15px] font-semibold text-[#111]">Services</h3>
            <button onClick={() => setServices([...services, emptyService()])} data-testid="form-add-service" className="flex items-center gap-1 text-[13px] font-semibold text-[#111]"><Plus size={15} /> Add service</button>
          </div>
          <div className="space-y-4">
            {services.map((s, i) => (
              <div key={i} className="rounded-[12px] border border-[#eee] p-4" data-testid={`service-row-${i}`}>
                <div className="flex items-end gap-3 mb-3">
                  <div className="flex-1">
                    <Field label="Service name"><Input value={s.name} data-testid={`service-name-${i}`} onChange={(e) => setServices(services.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="e.g. Detailed Reading" /></Field>
                  </div>
                  {services.length > 1 && (
                    <button onClick={() => setServices(services.filter((_, j) => j !== i))} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border border-[#e7b3ab] text-[#c0392b]"><Trash2 size={16} /></button>
                  )}
                </div>
                <div className="space-y-2 mb-3">
                  {[
                    { key: "tier1", n: 1 },
                    { key: "tier2", n: 2 },
                    { key: "tier3", n: 3 },
                  ].map(({ key, n }) => (
                    <div key={key} className="rounded-[8px] bg-[#f9f9f9] p-3">
                      <Field label={`Tier ${n} Price (₹)`}><Input type="number" value={s[`${key}_price_inr`] ?? ""} onChange={(e) => setServices(services.map((x, j) => j === i ? { ...x, [`${key}_price_inr`]: e.target.value } : x))} placeholder="0" /></Field>
                      <div className="mt-2">
                        <p className="mb-1.5 text-[11px] text-[#999]">DURATION (IN MINS)</p>
                        <div className="flex gap-1.5">
                          {DURATIONS.map((d) => (
                            <Chip key={d} selected={Number(s[`${key}_duration_minutes`]) === d} onClick={() => setServices(services.map((x, j) => j === i ? { ...x, [`${key}_duration_minutes`]: d } : x))} testid={`service-${i}-${key}-dur-${d}`}>{d}</Chip>
                          ))}
                        </div>
                      </div>
                      <div className="mt-2">
                        <p className="mb-1.5 text-[11px] text-[#999]">MODE</p>
                        <div className="flex flex-wrap gap-1.5">
                          {MODES.map(([label, val]) => (
                            <Chip key={val} selected={(s[`${key}_consultation_modes`] || []).includes(val)} onClick={() => setServices(services.map((x, j) => j === i ? { ...x, [`${key}_consultation_modes`]: toggle(x[`${key}_consultation_modes`] || [], val) } : x))} testid={`service-${i}-${key}-mode-${val}`}>{label}</Chip>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </AdminCard>

        {/* Payout */}
        <AdminCard className="p-6" testid="form-payout">
          <h3 className="mb-4 text-[15px] font-semibold text-[#111]">Payout details</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="UPI ID"><Input value={payout.payout_upi_id} onChange={(e) => setPayout({ ...payout, payout_upi_id: e.target.value })} placeholder="name@upi" /></Field>
            <Field label="Account holder name"><Input value={payout.payout_account_name} onChange={(e) => setPayout({ ...payout, payout_account_name: e.target.value })} /></Field>
            <Field label="Account number"><Input value={payout.payout_account_number} onChange={(e) => setPayout({ ...payout, payout_account_number: e.target.value })} /></Field>
            <Field label="IFSC"><Input value={payout.payout_ifsc} onChange={(e) => setPayout({ ...payout, payout_ifsc: e.target.value })} /></Field>
          </div>
        </AdminCard>

        {/* Settings */}
        <AdminCard className="p-6" testid="form-settings">
          <h3 className="mb-4 text-[15px] font-semibold text-[#111]">Visibility</h3>
          <div className="flex items-center justify-between py-2">
            <div><p className="text-[14px] font-medium text-[#111]">Active &amp; publicly bookable</p><p className="text-[12px] text-[#999]">Clients can find and book this astrologer.</p></div>
            <Switch on={active} onChange={setActive} testid="form-active" />
          </div>
          {!isEdit && (
            <div className="flex items-center justify-between border-t border-[#f0ede8] py-2">
              <div><p className="text-[14px] font-medium text-[#111]">Send invite email</p><p className="text-[12px] text-[#999]">Email them to sign in with this address and manage their profile.</p></div>
              <Switch on={sendInvite} onChange={setSendInvite} testid="form-invite" />
            </div>
          )}
        </AdminCard>

        {/* Availability — edit mode only (needs an existing astrologer). Same calendar the astrologer uses. */}
        {isEdit && (
          <AdminCard className="p-6" testid="form-availability">
            <h3 className="mb-1 text-[15px] font-semibold text-[#111]">Availability</h3>
            <p className="mb-4 text-[12px] text-[#999]">Set weekly hours and per-date custom/blocked days. Changes here save automatically.</p>
            <div className="relative min-h-[480px]">
              <AvailabilityManager client={adminApi} basePath={`/admin/astrologers/${aid}/availability`} />
            </div>
          </AdminCard>
        )}
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[#e9e6e1] bg-white/90 px-6 py-4 backdrop-blur md:left-[var(--admin-sidebar,260px)]">
        <div className="mx-auto flex max-w-5xl justify-end">
          <Btn onClick={submit} busy={saving} testid="form-submit">{isEdit ? "Save changes" : "Create & set availability"}</Btn>
        </div>
      </div>
    </div>
  );
}
